/**
 * React hooks for permission operations.
 *
 * These wrap the orval-generated permissions client (P-9 / D16) — the request
 * bodies are the generated, enum-typed models, so `relation` is validated as the
 * single-sourced enum end to end. The custom query keys are preserved so the
 * sharing UI (ShareDialog, SharingAvatarStack, useTaskPermissions) keeps its
 * cache-invalidation contract.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  checkPermissionApiV1PermissionsCheckPost,
  grantAccessApiV1PermissionsGrantPost,
  revokeAccessApiV1PermissionsRevokeDelete,
  getEntityAccessListApiV1PermissionsAccessObjectTypeObjectIdGet,
  transferOwnershipApiV1PermissionsTransferOwnershipPost,
  useListVisibilityModesApiV1PermissionsVisibilityModesGet,
  getAdminWriteElevationApiV1PermissionsAdminElevationObjectTypeObjectIdGet,
  grantAdminWriteElevationApiV1PermissionsAdminElevationPost,
  releaseAdminWriteElevationApiV1PermissionsAdminElevationObjectTypeObjectIdDelete,
} from '../../../generated/api/permissions/permissions';
import type { PermissionCheckResponse } from '../../../generated/api/models/permissionCheckResponse';
import type { RelationType } from '../../../generated/api/models/relationType';
import type { VisibilityMode } from '../../../generated/config-enums';
import type { AccessListResponse, SubjectType } from './types';

interface UseEntityAccessOptions {
  objectType: string;
  objectId: string;
  enabled?: boolean;
}

/** Fetch access list for an entity */
export function useEntityAccess({ objectType, objectId, enabled = true }: UseEntityAccessOptions) {
  return useQuery({
    queryKey: ['permissions', 'access', objectType, objectId],
    queryFn: async () => {
      const data = await getEntityAccessListApiV1PermissionsAccessObjectTypeObjectIdGet(
        objectType,
        objectId,
      );
      return data as AccessListResponse;
    },
    enabled: enabled && !!objectType && !!objectId,
  });
}

interface GrantAccessParams {
  subjectType: SubjectType;
  subjectId: string;
  relation: RelationType;
  objectType: string;
  objectId: string;
  expiresAt?: string;
}

/** Grant access to a subject */
export function useGrantAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subjectType,
      subjectId,
      relation,
      objectType,
      objectId,
      expiresAt,
    }: GrantAccessParams) =>
      grantAccessApiV1PermissionsGrantPost({
        subject: { type: subjectType, id: subjectId },
        relation,
        resource: { type: objectType, id: objectId },
        expires_at: expiresAt ?? null,
      }),
    onSuccess: (_, { objectType, objectId }) => {
      queryClient.invalidateQueries({
        queryKey: ['permissions', 'access', objectType, objectId],
      });
    },
  });
}

interface RevokeAccessParams {
  subjectType: SubjectType;
  subjectId: string;
  relation?: RelationType;
  objectType: string;
  objectId: string;
}

/** Revoke access from a subject */
export function useRevokeAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subjectType,
      subjectId,
      relation,
      objectType,
      objectId,
    }: RevokeAccessParams) =>
      revokeAccessApiV1PermissionsRevokeDelete({
        subject: { type: subjectType, id: subjectId },
        relation: relation ?? null,
        resource: { type: objectType, id: objectId },
      }),
    onSuccess: (_, { objectType, objectId }) => {
      queryClient.invalidateQueries({
        queryKey: ['permissions', 'access', objectType, objectId],
      });
    },
  });
}

interface TransferOwnershipParams {
  toUserId: string;
  objectType: string;
  objectId: string;
}

/**
 * Transfer ownership of an entity to another user. The owner can transfer their
 * own records; a tenant admin can additionally recover a record whose current
 * owner is non-active (backend enforces both — design §4.1).
 */
export function useTransferOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ toUserId, objectType, objectId }: TransferOwnershipParams) =>
      transferOwnershipApiV1PermissionsTransferOwnershipPost({
        to_user_id: toUserId,
        resource: { type: objectType, id: objectId },
      }),
    onSuccess: (_, { objectType, objectId }) => {
      queryClient.invalidateQueries({
        queryKey: ['permissions', 'access', objectType, objectId],
      });
    },
  });
}

interface CheckPermissionParams {
  action: string;
  objectType: string;
  objectId: string;
  userId?: string;
}

/** Check if user has permission */
export function useCheckPermission({ action, objectType, objectId, userId }: CheckPermissionParams) {
  return useQuery({
    queryKey: ['permissions', 'check', objectType, objectId, action, userId],
    queryFn: (): Promise<PermissionCheckResponse> =>
      checkPermissionApiV1PermissionsCheckPost({
        user_id: userId ?? null,
        action,
        resource: { type: objectType, id: objectId },
      }),
    enabled: !!objectType && !!objectId && !!action,
  });
}

// ============================================================================
// Visibility modes (Sharing Infra P-4 / capstone C3)
//
// The frontend's per-object_type visibility-mode signal. Sharing chrome
// (Share buttons, OwnershipIndicator, restricted empty states) can be gated on
// whether a tenant actually restricted a module — an `everyone`-mode SMB that
// never opted into sharing should not see Share affordances that do nothing.
//
// This is the enabling primitive only; it changes NO chrome behaviour (the
// follow-up WI consumes it). One tenant-level request feeds a shared cache, so
// there is no extra request per record — every component reads the same map.
// ============================================================================

/**
 * The mode the resolver actually compiles against. `custom` is a reserved
 * no-op until the rules engine lands and collapses to `owner_and_shared`
 * (mirrors the backend `effective_mode`). Every other mode passes through.
 */
export function effectiveVisibilityMode(mode: VisibilityMode): VisibilityMode {
  return mode === 'custom' ? 'owner_and_shared' : mode;
}

/**
 * The "open" visibility modes — every member sees every record, so per-record
 * sharing chrome (Share button, OwnershipIndicator) is a no-op. `everyone` is
 * view-open (only owner/shared can edit); `everyone_edit` additionally lets every
 * member edit. Mirrors the backend `AccessResolver.OPEN_VISIBILITY_MODES`, so the
 * "is this type restricted?" signal is single-sourced across both open modes.
 */
export function isOpenVisibilityMode(mode: VisibilityMode): boolean {
  return mode === 'everyone' || mode === 'everyone_edit';
}

/** The default effective mode when a tenant has no row for an object_type —
 * preserves every not-yet-onboarded module's current behaviour. */
const DEFAULT_VISIBILITY_MODE: VisibilityMode = 'everyone';

// Tenant-scoped, changes only when an admin flips a module's setting — cache
// aggressively so the map is warm and read synchronously by many components.
const VISIBILITY_MODES_STALE_TIME = 5 * 60 * 1000;

/**
 * Fetch the tenant's visibility mode for every visibility-configurable
 * object_type in a single request, exposed as a `Map<object_type, mode>`.
 * Shared cache — all `useVisibilityMode` callers read this one query.
 */
export function useVisibilityModes() {
  const { data, isLoading, isError } =
    useListVisibilityModesApiV1PermissionsVisibilityModesGet({
      query: { staleTime: VISIBILITY_MODES_STALE_TIME },
    });

  const { modeByObjectType, overrideByObjectType, exemptByObjectType } = useMemo(() => {
    const modeMap = new Map<string, VisibilityMode>();
    const overrideMap = new Map<string, boolean>();
    const exemptMap = new Map<string, boolean>();
    for (const info of data?.modes ?? []) {
      modeMap.set(info.object_type, info.mode);
      overrideMap.set(info.object_type, info.admin_override_active);
      exemptMap.set(info.object_type, info.admin_override_exempt);
    }
    return {
      modeByObjectType: modeMap,
      overrideByObjectType: overrideMap,
      exemptByObjectType: exemptMap,
    };
  }, [data]);

  return { modeByObjectType, overrideByObjectType, exemptByObjectType, isLoading, isError };
}

export interface UseVisibilityModeResult {
  /** The stored mode (`everyone` default when the tenant has no row). */
  mode: VisibilityMode;
  /** `mode` with the reserved `custom` no-op collapsed to `owner_and_shared`. */
  effectiveMode: VisibilityMode;
  /** True when the tenant restricted this object_type (effective mode is NOT an
   * open mode — i.e. not `everyone` / `everyone_edit`, see {@link isOpenVisibilityMode}) —
   * the signal sharing chrome is gated on. In both open modes every member already
   * sees every record, so sharing chrome is a no-op and stays hidden. */
  isRestricted: boolean;
  /**
   * True when this object_type exposes a configurable visibility mode (the map
   * carries a row for it) — i.e. `everyone` mode genuinely means "no per-record
   * filtering", so sharing chrome (Share button, OwnershipIndicator) is a no-op
   * there. False for legacy always-filtered ReBAC types (e.g. `tasks.workspace`)
   * that are absent from the map — their Share affordance stays meaningful and
   * must NOT be mode-gated. Also false while loading / on error (map empty), so a
   * chrome gate keyed on `isConfigurable && !isRestricted` fails OPEN. */
  isConfigurable: boolean;
  /**
   * True when the §4.1 tenant-admin visibility override is ACTIVE for the current
   * caller on this object_type — they are seeing every row via the audited
   * universal-viewer floor, beyond their own owner/shared scope. Resolved by the
   * backend on the effective identity, so it is always false for non-admins and
   * acting-as agents. Drives {@link AdminOverrideBanner}. False while loading /
   * on error (the map is empty), so a gate keyed on it fails closed (no banner). */
  adminOverrideActive: boolean;
  /** True when this object_type opts out of the §4.1 JIT admin write-override
   * (config `sharing.adminOverrideExempt`) — the "Edit as admin" elevation is
   * unavailable there. False while loading / on error (fails closed together
   * with `adminOverrideActive`, which is what actually gates the affordance). */
  adminOverrideExempt: boolean;
  isLoading: boolean;
  /** The fetch failed. `mode` falls back to the `everyone` default while this is
   * true, so a chrome-gating caller can choose to fail closed rather than treat a
   * transient error as "unrestricted". */
  isError: boolean;
}

/**
 * The effective visibility mode for one `object_type` for the current tenant.
 * Reads the shared tenant-level map — no per-record request. Post-load, `mode`
 * / `effectiveMode` / `isRestricted` / `isConfigurable` are available synchronously.
 */
export function useVisibilityMode(objectType: string): UseVisibilityModeResult {
  const { modeByObjectType, overrideByObjectType, exemptByObjectType, isLoading, isError } =
    useVisibilityModes();
  const isConfigurable = modeByObjectType.has(objectType);
  const mode = modeByObjectType.get(objectType) ?? DEFAULT_VISIBILITY_MODE;
  const effectiveMode = effectiveVisibilityMode(mode);
  return {
    mode,
    effectiveMode,
    isRestricted: !isOpenVisibilityMode(effectiveMode),
    isConfigurable,
    adminOverrideActive: overrideByObjectType.get(objectType) ?? false,
    adminOverrideExempt: exemptByObjectType.get(objectType) ?? false,
    isLoading,
    isError,
  };
}

/** The URL query param that persists an admin's "narrow to mine" choice for a
 * governed list. Value `'own'` scopes the list to the admin's own owner/shared set
 * (matching the backend `?admin_scope=own` contract); absent = the default §4.1
 * audited override view. Kept in the URL so the choice is refresh-stable, linkable,
 * and naturally per-list (each list is its own route). */
export const ADMIN_SCOPE_PARAM = 'admin_scope';
const ADMIN_SCOPE_OWN = 'own';

export interface UseAdminListScopeResult {
  /** True when the §4.1 tenant-admin override is active for this caller on this
   * `object_type` — i.e. the "narrow to mine" toggle is meaningful for them.
   * Always false for non-admins / acting-as agents / everyone-mode types. */
  adminOverrideActive: boolean;
  /** True when the admin has toggled this list to their own owner/shared scope. */
  narrowed: boolean;
  /** Thread this into the list query's `admin_scope` param: `'own'` when narrowed,
   * else `undefined` (default audited override view). `undefined` also for any
   * caller who cannot narrow, so a non-admin's query is byte-for-byte unchanged. */
  adminScope: typeof ADMIN_SCOPE_OWN | undefined;
  /** Toggle between the audited override view (`false`) and own scope (`true`). */
  setNarrowed: (narrowed: boolean) => void;
}

/**
 * The governed-list "narrow to mine" control state (§4.1 follow-up). A Full-Access
 * admin viewing a restricted governed list under the audited override may opt into
 * their OWN owner/shared scope — this hook reads/writes that choice via the
 * {@link ADMIN_SCOPE_PARAM} URL param and hands the list query the `admin_scope`
 * value to send.
 *
 * Gated on {@link useVisibilityMode}'s `adminOverrideActive` (resolved by the
 * backend on the effective identity): only an admin whose override is actually
 * active can narrow, so a non-admin / acting-as agent always gets
 * `adminScope: undefined` and an inert toggle. Drives the {@link AdminOverrideBanner}
 * toggle and the list page's query.
 */
export function useAdminListScope(objectType: string): UseAdminListScopeResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const { adminOverrideActive } = useVisibilityMode(objectType);

  const requested = searchParams.get(ADMIN_SCOPE_PARAM) === ADMIN_SCOPE_OWN;
  // Only honour the narrow request for an admin whose override is active — never
  // let the raw URL param scope a non-admin (whose list is already their own set).
  const narrowed = adminOverrideActive && requested;
  const adminScope = narrowed ? ADMIN_SCOPE_OWN : undefined;

  const setNarrowed = useCallback(
    (next: boolean) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next) params.set(ADMIN_SCOPE_PARAM, ADMIN_SCOPE_OWN);
          else params.delete(ADMIN_SCOPE_PARAM);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { adminOverrideActive, narrowed, adminScope, setNarrowed };
}

/**
 * Whether sharing chrome (Share button/menu, OwnershipIndicator) should be shown
 * for an `object_type`, given the tenant's visibility mode. Hidden ONLY when the
 * type is a configurable one the tenant deliberately left on `everyone` (chrome
 * would be a no-op there — capstone §9 C3/C4). Legacy always-filtered types and
 * the loading/error window are not configurable-in-map, so chrome stays visible
 * (fail open — never hide a working Share affordance on a transient miss).
 */
export function useSharingChromeVisible(objectType: string): boolean {
  const { isConfigurable, isRestricted } = useVisibilityMode(objectType);
  return !isConfigurable || isRestricted;
}

/** Query key for the caller's §4.1 JIT write-elevation state on one record. */
const elevationQueryKey = (objectType: string, objectId: string) => [
  'permissions',
  'admin-elevation',
  objectType,
  objectId,
];

export interface UseAdminWriteElevationResult {
  /** True when the "Edit as admin" affordance may be offered here: the §4.1
   * admin override is active for the caller on this object_type (backend-resolved,
   * effective identity) AND the type is not override-exempt AND the caller
   * supplied a record. Fails closed while loading / on error. */
  eligible: boolean;
  /** True when the caller currently holds an active write elevation on this record. */
  active: boolean;
  /** ISO expiry of the active elevation, or null. */
  expiresAt: string | null;
  /** The record-bound step-up action the elevation POST requires — pass to
   * `SensitiveActionDialog`'s `action` prop. */
  stepUpAction: string;
  /** Grant (or refresh) the elevation with a spent step-up proof. */
  elevate: (stepUpProof: string, reason?: string) => Promise<void>;
  /** End the elevation early ("Done editing"). */
  release: () => Promise<void>;
  isPending: boolean;
}

/**
 * The §4.1 JIT admin write-override ("Edit as admin") state + actions for one
 * governed record (WI a73e09ac). Drives {@link ViewOnlyBanner}'s step-up button
 * and its persistent "Admin override active" state.
 *
 * Cost-free for everyone else: the state query runs ONLY when the backend
 * reports `admin_override_active` for the caller on this object_type (never for
 * non-admins, acting-as agents, or `everyone`-mode types) and the type is not
 * override-exempt. While an elevation is active the query re-polls each minute
 * so the banner (and edit UI) drop back to read-only when the window expires.
 *
 * Granting/releasing invalidates the ENTIRE query cache deliberately: the
 * elevation changes `user_relation` on whatever detail/list payload carries this
 * record, and those keys are per-module — a global refetch after a rare,
 * explicit admin step-up is the one reliable way to unlock (and re-lock) every
 * surface at once.
 */
export function useAdminWriteElevation(
  objectType?: string,
  objectId?: string,
): UseAdminWriteElevationResult {
  const queryClient = useQueryClient();
  const { adminOverrideActive, adminOverrideExempt } = useVisibilityMode(objectType ?? '');
  const eligible =
    !!objectType && !!objectId && adminOverrideActive && !adminOverrideExempt;

  const { data } = useQuery({
    queryKey: elevationQueryKey(objectType ?? '', objectId ?? ''),
    queryFn: () =>
      getAdminWriteElevationApiV1PermissionsAdminElevationObjectTypeObjectIdGet(
        objectType as string,
        objectId as string,
      ),
    enabled: eligible,
    refetchInterval: (query) => (query.state.data?.active ? 60_000 : false),
  });

  const elevateMutation = useMutation({
    mutationFn: ({ stepUpProof, reason }: { stepUpProof: string; reason?: string }) =>
      grantAdminWriteElevationApiV1PermissionsAdminElevationPost({
        object_type: objectType as string,
        object_id: objectId as string,
        step_up_proof: stepUpProof,
        reason: reason ?? null,
      }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const releaseMutation = useMutation({
    mutationFn: () =>
      releaseAdminWriteElevationApiV1PermissionsAdminElevationObjectTypeObjectIdDelete(
        objectType as string,
        objectId as string,
      ),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const elevate = useCallback(
    async (stepUpProof: string, reason?: string) => {
      await elevateMutation.mutateAsync({ stepUpProof, reason });
    },
    [elevateMutation],
  );
  const release = useCallback(async () => {
    await releaseMutation.mutateAsync();
  }, [releaseMutation]);

  return {
    eligible,
    active: data?.active ?? false,
    expiresAt: data?.expires_at ?? null,
    stepUpAction: `permissions.admin_write_elevation:${objectType ?? ''}:${objectId ?? ''}`,
    elevate,
    release,
    isPending: elevateMutation.isPending || releaseMutation.isPending,
  };
}
